import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { ChevronDown, Info, MoreVertical, Plus, Sparkles, Trash2, Undo2 } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Modal, ConfirmDialog } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { useLocation } from '@/shared/hooks/useLocation';
import { formatRelativeTime } from '@/shared/utils/format';
import patterns from '@/shared/ui/styles/patterns.module.css';
import {
  apiErrorMessage,
  referralsApi,
  type BodyRegionSelection,
  type DeliveryMethod,
  type DirectoryEntry,
  type RecipientOption,
  type ReferralDetail,
  type ReferralDraftInput,
  type Urgency,
} from '../api/referralsApi';
import { useReferral, useReferralMutations, useReferralTemplate } from '../hooks';
import { AttachmentDropzone, BodyRegionPicker, ReasonChips, RecipientPicker, ReferralSlipView, ToothChart } from '../components';
import chips from '../components/chips.module.css';
import styles from './NewReferralPage.module.css';

/** One office on the slip. `recipientId` is set once the server row exists. */
type Picked = {
  entry: DirectoryEntry;
  recipientId: string | null;
  preferredProviderId: string | null;
  preferredProviderName: string;
};

type Form = {
  recipients: Picked[];
  patientFollowUp: boolean;
  referringStaffMemberId: string;
  referringFirst: string;
  referringLast: string;
  patientFirstName: string;
  patientLastName: string;
  patientPhone: string;
  textPatientCopy: boolean;
  patientDob: string;
  patientEmail: string;
  insurance: string;
  memberId: string;
  preferredLanguage: string;
  address: string;
  patientNotes: string;
  reasons: string[];
  teeth: string[];
  bodyRegions: BodyRegionSelection[];
  comment: string;
  urgency: Urgency;
};

const EMPTY: Form = {
  recipients: [],
  patientFollowUp: true,
  referringStaffMemberId: '',
  referringFirst: '',
  referringLast: '',
  patientFirstName: '',
  patientLastName: '',
  patientPhone: '',
  textPatientCopy: true,
  patientDob: '',
  patientEmail: '',
  insurance: '',
  memberId: '',
  preferredLanguage: '',
  address: '',
  patientNotes: '',
  reasons: [],
  teeth: [],
  bodyRegions: [],
  comment: '',
  urgency: 'routine',
};

function splitName(full: string | null): [string, string] {
  if (!full) return ['', ''];
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return [parts[0], ''];
  return [parts.slice(0, -1).join(' '), parts[parts.length - 1]];
}

function fromDetail(d: ReferralDetail, recipients: Picked[]): Form {
  const [referringFirst, referringLast] = splitName(d.referringDoctorName);
  return {
    recipients,
    patientFollowUp: d.patientFollowUp,
    referringStaffMemberId: d.referringStaffMemberId ?? '',
    referringFirst,
    referringLast,
    patientFirstName: d.patientFirstName,
    patientLastName: d.patientLastName,
    patientPhone: d.patientPhone ?? '',
    textPatientCopy: d.textPatientCopy,
    patientDob: d.patientDob ?? '',
    patientEmail: d.patientEmail ?? '',
    insurance: d.patientDetails?.insurance ?? '',
    memberId: d.patientDetails?.memberId ?? '',
    preferredLanguage: d.patientDetails?.preferredLanguage ?? '',
    address: d.patientDetails?.address ?? '',
    patientNotes: d.patientDetails?.notes ?? '',
    reasons: d.reasons,
    teeth: d.teeth,
    bodyRegions: d.bodyRegions,
    comment: d.comment ?? '',
    urgency: d.urgency,
  };
}

function toInput(f: Form): Omit<ReferralDraftInput, 'locationId'> {
  const referringDoctorName = [f.referringFirst, f.referringLast].filter(Boolean).join(' ').trim();
  const details = {
    ...(f.insurance.trim() ? { insurance: f.insurance.trim() } : {}),
    ...(f.memberId.trim() ? { memberId: f.memberId.trim() } : {}),
    ...(f.preferredLanguage.trim() ? { preferredLanguage: f.preferredLanguage.trim() } : {}),
    ...(f.address.trim() ? { address: f.address.trim() } : {}),
    ...(f.patientNotes.trim() ? { notes: f.patientNotes.trim() } : {}),
  };
  return {
    patientFollowUp: f.patientFollowUp,
    referringDoctorName: referringDoctorName || null,
    referringStaffMemberId: f.referringStaffMemberId || null,
    patientFirstName: f.patientFirstName.trim(),
    patientLastName: f.patientLastName.trim(),
    patientPhone: f.patientPhone.trim() || null,
    patientEmail: f.patientEmail.trim() || null,
    patientDob: f.patientDob || null,
    patientDetails: Object.keys(details).length ? details : null,
    textPatientCopy: f.textPatientCopy,
    reasons: f.reasons,
    teeth: f.teeth,
    bodyRegions: f.bodyRegions,
    comment: f.comment.trim() || null,
    urgency: f.urgency,
  };
}

/**
 * The referral slip. A draft row is created the moment the page opens and every edit autosaves
 * to it, so nothing is lost on a refresh; "Preview & Send" shows the slip exactly as the receiving
 * office will see it, then sends.
 */
export function NewReferralPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { currentLocation } = useLocation();
  const { create, update, send, remove, addRecipient, updateRecipient, removeRecipient, locationId } = useReferralMutations();
  const [draftId, setDraftId] = useState<string | null>(routeId ?? null);
  // Also the source of the attachment list, so it follows a draft created on this page.
  const existing = useReferral(draftId ?? routeId);
  const [form, setForm] = useState<Form>(EMPTY);
  const [loadedFromServer, setLoadedFromServer] = useState(Boolean(routeId) === false);
  const [numbering, setNumbering] = useState<'universal' | 'fdi'>('universal');
  const firstOffice = form.recipients[0]?.entry ?? null;
  const template = useReferralTemplate(firstOffice?.id ?? null);
  const [moreInfo, setMoreInfo] = useState(false);
  const [preferredOpen, setPreferredOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [menu, setMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [undoComment, setUndoComment] = useState<string | null>(null);
  const [previewDetail, setPreviewDetail] = useState<ReferralDetail | null>(null);
  // Per office: how its copy goes out.
  const [methods, setMethods] = useState<Record<string, DeliveryMethod[]>>({});
  const [addEmailFor, setAddEmailFor] = useState<DirectoryEntry | null>(null);
  // "Add their email" from the preview: once saved, come straight back to Preview & Send.
  const [resumePreview, setResumePreview] = useState(false);
  const dirtyRef = useRef(false);
  const lastSaveErrorRef = useRef<string | null>(null);
  const creatingRef = useRef<Promise<void> | null>(null);
  const latestFormRef = useRef(form);
  latestFormRef.current = form;

  // Load an existing draft (…/referrals/:id/edit).
  useEffect(() => {
    if (!routeId || !existing.data || loadedFromServer) return;
    const d = existing.data;
    if (d.status !== 'draft') {
      navigate(`/referrals/${d.id}`, { replace: true });
      return;
    }
    const load = async () => {
      const picked = (
        await Promise.all(
          d.recipients.map(async (r): Promise<Picked | null> => {
            const entry = await referralsApi.getDirectoryPractice(locationId, r.practice.id).catch(() => null);
            return entry
              ? { entry, recipientId: r.id, preferredProviderId: r.preferredProviderId, preferredProviderName: r.preferredProviderName ?? '' }
              : null;
          }),
        )
      ).filter((p): p is Picked => p !== null);
      setForm(fromDetail(d, picked));
      setLastSavedAt(new Date(d.updatedAt));
      setLoadedFromServer(true);
    };
    void load();
  }, [routeId, existing.data, loadedFromServer, locationId, navigate]);

  useEffect(() => {
    if (template.data?.settings.toothNumbering) setNumbering(template.data.settings.toothNumbering);
  }, [template.data?.settings.toothNumbering]);

  /**
   * The draft row exists only once there is something to keep. Opening the page creates nothing;
   * the first edit (or the first file) does. Returns the id, creating the row on demand.
   */
  const draftIdRef = useRef<string | null>(draftId);
  draftIdRef.current = draftId;
  const ensureDraft = useCallback(async (): Promise<string> => {
    if (draftIdRef.current) return draftIdRef.current;
    if (creatingRef.current) {
      // Another caller is already creating it; wait for that one.
      await creatingRef.current;
      if (draftIdRef.current) return draftIdRef.current;
    }
    const pending = create.mutateAsync(toInput(latestFormRef.current)).then((d) => {
      draftIdRef.current = d.id;
      setDraftId(d.id);
      setLastSavedAt(new Date(d.createdAt));
      window.history.replaceState(null, '', `/referrals/${d.id}/edit`);
      return d.id;
    });
    creatingRef.current = pending.then(() => undefined, () => undefined);
    try {
      return await pending;
    } finally {
      creatingRef.current = null;
    }
  }, [create]);

  const persist = useCallback(async () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    setSaving(true);
    try {
      if (!draftIdRef.current) {
        // First edit: the create carries the current form, so no follow-up PATCH is needed.
        await ensureDraft();
      } else {
        await update.mutateAsync({ id: draftIdRef.current, input: toInput(latestFormRef.current) });
      }
      setLastSavedAt(new Date());
      lastSaveErrorRef.current = null;
    } catch (err) {
      dirtyRef.current = true;
      // One toast per distinct problem, not one per keystroke.
      const message = apiErrorMessage(err, 'Could not save the draft');
      if (lastSaveErrorRef.current !== message) {
        lastSaveErrorRef.current = message;
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  }, [ensureDraft, update, toast]);

  // Leaving a draft that is still blank (no edits kept, no files) removes it again, so a look at
  // the page never leaves a row behind.
  const removeRef = useRef(remove);
  removeRef.current = remove;
  const attachmentCountRef = useRef(0);
  useEffect(() => {
    return () => {
      const id = draftIdRef.current;
      if (id && isBlank(latestFormRef.current) && attachmentCountRef.current === 0) {
        removeRef.current.mutateAsync(id).catch(() => undefined);
      }
    };
  }, []);

  // Autosave 800ms after the last edit.
  useEffect(() => {
    if (!dirtyRef.current) return;
    const t = setTimeout(() => void persist(), 800);
    return () => clearTimeout(t);
  }, [form, persist]);

  const patch = (p: Partial<Form>) => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, ...p }));
  };

  /** Offices live in their own rows (POST /recipients), not in the autosaved draft body. */
  const addOffice = async (entry: DirectoryEntry) => {
    if (!entry.id || form.recipients.some((p) => p.entry.id === entry.id)) return;
    setForm((f) => ({ ...f, recipients: [...f.recipients, { entry, recipientId: null, preferredProviderId: null, preferredProviderName: '' }] }));
    try {
      const id = await ensureDraft();
      const detail = await addRecipient.mutateAsync({ id, input: { referralPracticeId: entry.id } });
      const row = detail.recipients.find((r) => r.practice.id === entry.id);
      setForm((f) => ({ ...f, recipients: f.recipients.map((p) => (p.entry.id === entry.id ? { ...p, recipientId: row?.id ?? null } : p)) }));
      setLastSavedAt(new Date());
    } catch (err) {
      setForm((f) => ({ ...f, recipients: f.recipients.filter((p) => p.entry.id !== entry.id) }));
      toast.error(apiErrorMessage(err, 'Could not add that office'));
    }
  };
  const removeOffice = async (entryId: string) => {
    const picked = form.recipients.find((p) => p.entry.id === entryId);
    setForm((f) => ({ ...f, recipients: f.recipients.filter((p) => p.entry.id !== entryId) }));
    if (picked?.recipientId && draftIdRef.current) {
      try {
        await removeRecipient.mutateAsync({ id: draftIdRef.current, recipientId: picked.recipientId });
      } catch (err) {
        toast.error(apiErrorMessage(err, 'Could not remove that office'));
      }
    }
  };
  const replaceOffice = (entry: DirectoryEntry) =>
    setForm((f) => ({ ...f, recipients: f.recipients.map((p) => (p.entry.id === entry.id ? { ...p, entry } : p)) }));
  const setPreferred = async (picked: Picked, providerId: string | null, providerName: string) => {
    setForm((f) => ({
      ...f,
      recipients: f.recipients.map((p) => (p.entry.id === picked.entry.id ? { ...p, preferredProviderId: providerId, preferredProviderName: providerName } : p)),
    }));
    if (picked.recipientId && draftIdRef.current) {
      await updateRecipient
        .mutateAsync({ id: draftIdRef.current, recipientId: picked.recipientId, input: { preferredProviderId: providerId, preferredProviderName: providerName || null } })
        .catch((err) => toast.error(apiErrorMessage(err, 'Could not save the preferred doctor')));
    }
  };

  const t = template.data;
  const vertical = t?.vertical ?? 'dental';
  attachmentCountRef.current = existingAttachments(existing.data, draftId).length;
  const reasonOptions = useMemo(() => t?.reasons ?? [], [t]);
  const providers = t?.providers ?? [];
  const senderCity = currentLocation?.city ?? null;

  const draftWithAi = async () => {
    if (!draftId || !t) return;
    setDrafting(true);
    try {
      const { comment } = await referralsApi.draftComment({
        locationId,
        recipientPracticeId: firstOffice?.id ?? null,
        reasons: form.reasons,
        teeth: form.teeth,
        bodyRegions: form.bodyRegions,
        notes: form.comment,
        urgency: form.urgency,
      });
      if (!comment) {
        toast.info('Nothing to draft from yet — pick a reason or jot a few words first.');
        return;
      }
      setUndoComment(form.comment);
      patch({ comment });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not draft the comment'));
    } finally {
      setDrafting(false);
    }
  };

  const openPreview = async () => {
    await persist();
    const id = await ensureDraft().catch(() => null);
    if (!id) return;
    try {
      const detail = await referralsApi.get(locationId, id);
      setPreviewDetail(detail);
      // Default each office to the best channel it supports: email, else text, else a printed slip.
      setMethods(
        Object.fromEntries(
          detail.recipients.map((r) => {
            const d = r.delivery;
            return [r.id, d?.email.available ? ['email'] : d?.text.available ? ['text'] : ['print']];
          }),
        ),
      );
      setPreview(true);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load the preview'));
    }
  };

  const allMethods = Object.values(methods).flat();
  const printOnly = allMethods.length > 0 && allMethods.every((m) => m === 'print');
  const everyOfficeCovered = (previewDetail?.recipients ?? []).every((r) => (methods[r.id] ?? []).length > 0);

  const confirmSend = async () => {
    if (!draftId || !previewDetail) return;
    try {
      const sent = await send.mutateAsync({
        id: draftId,
        input: { recipients: previewDetail.recipients.map((r) => ({ recipientId: r.id, methods: methods[r.id] ?? [] })) },
      });
      setPreview(false);
      const names = sent.recipients.map((r) => r.practice.name);
      toast.success(
        printOnly
          ? 'Referral saved. Print the office and patient copies from the next screen.'
          : `Referral sent to ${names.length > 1 ? `${names.length} offices` : (names[0] ?? 'the receiving office')}.`,
      );
      navigate(allMethods.includes('print') ? `/referrals/${sent.id}?print=1` : `/referrals/${sent.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send the referral'));
    }
  };

  const deleteDraft = async () => {
    if (!draftId) return;
    try {
      await remove.mutateAsync(draftId);
      toast.success('Draft deleted.');
      navigate('/referrals');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not delete the draft'));
    }
  };

  const missing: string[] = [];
  if (form.recipients.length === 0) missing.push('a receiving office');
  if (!form.patientFirstName.trim() || !form.patientLastName.trim()) missing.push("the patient's name");
  if (!form.patientPhone.trim()) missing.push("the patient's phone");
  if (form.reasons.length === 0 && !form.comment.trim()) missing.push('a reason or comment');

  if (!locationId || (routeId && !loadedFromServer) || template.isLoading) {
    return (
      <div className={patterns.page}>
        <Skeleton height={56} />
        <Skeleton height={420} />
      </div>
    );
  }

  return (
    <div className={clsx(patterns.page, styles.page)}>
      <div className={styles.bar}>
        <div className={styles.barTitle}>You haven't sent this referral</div>
        <div className={styles.barRight}>
          <span className={styles.saved}>
            {saving ? 'Saving…' : lastSavedAt ? `Last saved ${formatRelativeTime(lastSavedAt)}` : 'Draft saves as you type'}
          </span>
          <Button onClick={() => void openPreview()}>
            Preview & Send
          </Button>
          <div className={styles.menuWrap}>
            <button type="button" className={styles.menuBtn} aria-label="More" onClick={() => setMenu((m) => !m)}>
              <MoreVertical size={18} />
            </button>
            {menu && (
              <div className={styles.menu} onMouseLeave={() => setMenu(false)}>
                <button type="button" className={styles.menuItem} onClick={() => navigate('/referrals')}>
                  Back to referrals
                </button>
                {draftId && (
                  <button type="button" className={clsx(styles.menuItem, styles.menuDanger)} onClick={() => setConfirmDelete(true)}>
                    <Trash2 size={14} /> Delete draft
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.sheet}>
        {t && (
          <RecipientPicker
            template={t}
            selected={form.recipients.map((p) => p.entry)}
            onAdd={(entry) => void addOffice(entry)}
            onRemove={(entryId) => void removeOffice(entryId)}
            onReplace={replaceOffice}
            reasons={form.reasons}
            comment={form.comment}
            senderCity={senderCity}
            editDetailsFor={addEmailFor}
            onEditDetailsDone={(saved) => {
              setAddEmailFor(null);
              if (saved && resumePreview) {
                setResumePreview(false);
                void openPreview();
              } else {
                setResumePreview(false);
              }
            }}
          />
        )}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            Referring doctor <Info size={13} className={styles.info} aria-label="Who the patient is coming from" />
          </h2>
          <div className={styles.row3}>
            {providers.length > 0 && (
              <Select
                label="From your team"
                fullWidth
                options={[{ value: '', label: 'Type a name instead' }, ...providers.map((p) => ({ value: p.id, label: p.name }))]}
                value={form.referringStaffMemberId}
                onChange={(e) => {
                  const p = providers.find((x) => x.id === e.target.value);
                  const [first, last] = splitName(p?.name ?? null);
                  patch({ referringStaffMemberId: e.target.value, ...(p ? { referringFirst: first, referringLast: last } : {}) });
                }}
              />
            )}
            <Input label="First Name" fullWidth value={form.referringFirst} onChange={(e) => patch({ referringFirst: e.target.value, referringStaffMemberId: '' })} />
            <Input label="Last Name" fullWidth value={form.referringLast} onChange={(e) => patch({ referringLast: e.target.value, referringStaffMemberId: '' })} />
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Patient information</h2>
          <div className={styles.row3}>
            <Input label="First Name" fullWidth value={form.patientFirstName} onChange={(e) => patch({ patientFirstName: e.target.value })} autoComplete="off" />
            <Input label="Last Name" fullWidth value={form.patientLastName} onChange={(e) => patch({ patientLastName: e.target.value })} autoComplete="off" />
            <div>
              <Input
                label="Phone"
                fullWidth
                type="tel"
                value={form.patientPhone}
                onChange={(e) => patch({ patientPhone: e.target.value })}
                placeholder="(415) 400-1180"
                autoComplete="off"
              />
              <label className={clsx(styles.toggle, !t?.patientText.available && styles.toggleOff)} title={t?.patientText.reason ?? undefined}>
                <input
                  type="checkbox"
                  checked={form.textPatientCopy && Boolean(t?.patientText.available)}
                  disabled={!t?.patientText.available}
                  onChange={(e) => patch({ textPatientCopy: e.target.checked })}
                />
                <span className={styles.toggleTrack} aria-hidden />
                Text patient a copy
                <Info size={12} className={styles.info} />
              </label>
              {t?.patientText.available && (
                <label className={clsx(styles.toggle, !form.textPatientCopy && styles.toggleOff)} title="A week after sending, ask the patient by text whether they booked, and with which office.">
                  <input
                    type="checkbox"
                    checked={form.patientFollowUp && form.textPatientCopy}
                    disabled={!form.textPatientCopy}
                    onChange={(e) => patch({ patientFollowUp: e.target.checked })}
                  />
                  <span className={styles.toggleTrack} aria-hidden />
                  Check in by text after a week
                  <Info size={12} className={styles.info} />
                </label>
              )}
              {!t?.patientText.available && t?.patientText.reason && <p className={styles.hint}>{t.patientText.reason}</p>}
            </div>
          </div>
          <button type="button" className={styles.expander} onClick={() => setMoreInfo((m) => !m)} aria-expanded={moreInfo}>
            {moreInfo ? <ChevronDown size={14} /> : <Plus size={14} />} Additional patient information
          </button>
          {moreInfo && (
            <div className={styles.row3}>
              <Input label="Date of birth" type="date" fullWidth value={form.patientDob} onChange={(e) => patch({ patientDob: e.target.value })} />
              <Input label="Email" type="email" fullWidth value={form.patientEmail} onChange={(e) => patch({ patientEmail: e.target.value })} />
              <Input label="Preferred language" fullWidth value={form.preferredLanguage} onChange={(e) => patch({ preferredLanguage: e.target.value })} />
              <Input label="Insurance" fullWidth value={form.insurance} onChange={(e) => patch({ insurance: e.target.value })} />
              <Input label="Member ID" fullWidth value={form.memberId} onChange={(e) => patch({ memberId: e.target.value })} />
              <Input label="Address" fullWidth value={form.address} onChange={(e) => patch({ address: e.target.value })} />
              <label className={clsx(patterns.field, styles.span3)}>
                <span className={patterns.fieldLabel}>Notes about the patient (anxiety, mobility, accommodations)</span>
                <textarea className={patterns.textarea} rows={2} value={form.patientNotes} onChange={(e) => patch({ patientNotes: e.target.value })} maxLength={1000} />
              </label>
            </div>
          )}
        </section>

        <div className={styles.split}>
          <div className={styles.splitLeft}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                Reason for Referral{firstOffice?.specialtyLabel ? ` (${firstOffice.specialtyLabel})` : ''}
                <Info size={13} className={styles.info} />
              </h2>
              {!firstOffice && <p className={styles.hint}>Common reasons will be available once you select a practice. You can still type your own.</p>}
              <ReasonChips options={reasonOptions} selected={form.reasons} onChange={(reasons) => patch({ reasons })} />
              <div className={styles.urgencyRow}>
                <span className={styles.urgencyLabel}>Urgency</span>
                {(['routine', 'soon', 'urgent'] as Urgency[]).map((u) => (
                  <button
                    key={u}
                    type="button"
                    className={clsx(chips.chip, chips.chipSmall, u === 'urgent' && chips.chipUrgent, u === 'soon' && chips.chipSoon, form.urgency === u && chips.chipOn)}
                    onClick={() => patch({ urgency: u })}
                    aria-pressed={form.urgency === u}
                  >
                    {u === 'routine' ? 'Routine' : u === 'soon' ? 'Soon' : 'Urgent'}
                  </button>
                ))}
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.commentHead}>
                <h2 className={styles.sectionTitle}>
                  Comment <Info size={13} className={styles.info} aria-label="What the receiving clinician reads" />
                </h2>
                <div className={styles.commentActions}>
                  {undoComment !== null && (
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<Undo2 size={13} />}
                      onClick={() => {
                        patch({ comment: undoComment });
                        setUndoComment(null);
                      }}
                    >
                      Undo
                    </Button>
                  )}
                  {t?.aiAvailable && (
                    <Button size="sm" variant="secondary" leftIcon={<Sparkles size={13} />} loading={drafting} onClick={() => void draftWithAi()}>
                      {form.comment.trim() ? 'Polish with AI' : 'Draft with AI'}
                    </Button>
                  )}
                </div>
              </div>
              <textarea
                className={clsx(patterns.textarea, styles.comment)}
                rows={6}
                value={form.comment}
                onChange={(e) => patch({ comment: e.target.value })}
                placeholder={
                  vertical === 'dental'
                    ? 'Severe pain on #30; non-restorable due to extensive decay. Recommend extraction.'
                    : 'Intermittent exertional chest pain for 3 weeks; abnormal ECG attached. Please evaluate.'
                }
                maxLength={4000}
              />
              <p className={styles.hint}>
                {t?.aiAvailable ? 'Jot shorthand and let AI write the clinical comment — it only uses what you give it.' : 'Keep it to what the receiving clinician needs before the first visit.'}
              </p>
            </section>

            {form.recipients.length > 0 && (
              <section className={styles.section}>
                <button type="button" className={styles.expander} onClick={() => setPreferredOpen((o) => !o)} aria-expanded={preferredOpen}>
                  {preferredOpen ? <ChevronDown size={14} /> : <Plus size={14} />} Choose preferred doctor
                  {!preferredOpen && form.recipients.some((p) => p.preferredProviderName) && (
                    <span className={styles.expanderValue}>{form.recipients.map((p) => p.preferredProviderName).filter(Boolean).join(', ')}</span>
                  )}
                </button>
                {preferredOpen &&
                  form.recipients.map((p) => (
                    <div key={p.entry.id} className={styles.preferredRow}>
                      {form.recipients.length > 1 && <span className={styles.preferredOffice}>{p.entry.name}</span>}
                      <div className={chips.row}>
                        <button
                          type="button"
                          className={clsx(chips.chip, !p.preferredProviderId && !p.preferredProviderName && chips.chipOn)}
                          onClick={() => void setPreferred(p, null, '')}
                        >
                          Any (first available)
                        </button>
                        {p.entry.providers.map((prov) => (
                          <button
                            key={prov.id}
                            type="button"
                            className={clsx(chips.chip, p.preferredProviderId === prov.id && chips.chipOn)}
                            onClick={() => void setPreferred(p, prov.id, prov.name)}
                          >
                            {prov.name}
                          </button>
                        ))}
                        <AddProviderInline practiceId={p.entry.id!} onAdded={replaceOffice} />
                      </div>
                    </div>
                  ))}
              </section>
            )}
          </div>

          <div className={styles.splitRight}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{vertical === 'dental' ? 'Teeth' : 'Affected area'}</h2>
              {vertical === 'dental' && t ? (
                <ToothChart teeth={t.teeth} selected={form.teeth} onChange={(teeth) => patch({ teeth })} numbering={numbering} onNumberingChange={setNumbering} />
              ) : t ? (
                <BodyRegionPicker regions={t.bodyRegions} selected={form.bodyRegions} onChange={(bodyRegions) => patch({ bodyRegions })} />
              ) : null}
            </section>
          </div>
        </div>

        <section className={styles.section}>
          <AttachmentDropzone
            attachments={existingAttachments(existing.data, draftId)}
            requestUpload={async (input) => referralsApi.requestUpload(locationId, await ensureDraft(), input)}
            confirmUpload={async (aid) => referralsApi.confirmUpload(locationId, await ensureDraft(), aid)}
            onDelete={draftId ? (aid) => referralsApi.deleteAttachment(locationId, draftId, aid).then(() => existing.refetch()) : undefined}
            onChanged={() => void existing.refetch()}
            disabled={!t?.attachmentsAvailable}
            disabledReason={t && !t.attachmentsAvailable ? 'File uploads are not configured on PracticeNova yet.' : null}
            filterSide="sender"
          />
        </section>
      </div>

      <Modal
        isOpen={preview}
        onClose={() => setPreview(false)}
        title="Preview & send"
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPreview(false)}>
              Keep editing
            </Button>
            <Button onClick={() => void confirmSend()} loading={send.isPending} disabled={missing.length > 0 || !everyOfficeCovered}>
              {printOnly ? 'Confirm and print' : 'Confirm and send'}
            </Button>
          </>
        }
      >
        {previewDetail && t && (
          <div className={styles.previewBody}>
            {missing.length > 0 && <div className={styles.previewWarn}>Before sending, add {missing.join(', ')}.</div>}
            {previewDetail.warnings.map((w) => (
              <div key={w} className={styles.previewWarn}>
                {w}
              </div>
            ))}
            {previewDetail.recipients.length > 1 && (
              <p className={styles.previewNote}>
                The patient can book with any of these {previewDetail.recipients.length} offices. Each gets its own copy and secure link; when one
                confirms a booking, the others are told the patient went elsewhere.
              </p>
            )}
            {previewDetail.recipients.map((r) =>
              r.delivery ? (
                <DeliveryChoices
                  key={r.id}
                  officeName={r.practice.name}
                  delivery={r.delivery}
                  methods={methods[r.id] ?? []}
                  onChange={(next) => setMethods((m) => ({ ...m, [r.id]: next }))}
                  onAddEmail={
                    !r.practice.email
                      ? () => {
                          const picked = form.recipients.find((p) => p.entry.id === r.practice.id);
                          if (!picked) return;
                          setPreview(false);
                          setResumePreview(true);
                          setAddEmailFor(picked.entry);
                        }
                      : undefined
                  }
                />
              ) : null,
            )}
            {form.textPatientCopy && t.patientText.available && (
              <p className={styles.previewNote}>
                The patient gets a text with {previewDetail.recipients.length > 1 ? 'every office to call' : 'where to call'}
                {form.patientFollowUp ? ', and a check-in a week later asking whether they booked' : ''}.
              </p>
            )}
            <ReferralSlipView referral={previewDetail} teeth={t.teeth} bodyRegions={t.bodyRegions} numbering={numbering} viewer="sender" />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => void deleteDraft()}
        title="Delete this draft?"
        message="The slip and any files you attached are removed. This cannot be undone."
        confirmLabel="Delete draft"
        variant="destructive"
        isLoading={remove.isPending}
      />
    </div>
  );
}

/** Nothing worth keeping: no office, no patient, no reason, no comment, no sites. */
function isBlank(f: Form): boolean {
  return (
    f.recipients.length === 0 &&
    !f.patientFirstName.trim() &&
    !f.patientLastName.trim() &&
    !f.patientPhone.trim() &&
    !f.patientEmail.trim() &&
    !f.patientDob &&
    !f.comment.trim() &&
    f.reasons.length === 0 &&
    f.teeth.length === 0 &&
    f.bodyRegions.length === 0 &&
    !f.referringFirst.trim() &&
    !f.referringLast.trim() &&
    !f.insurance.trim() &&
    !f.memberId.trim() &&
    !f.address.trim() &&
    !f.patientNotes.trim()
  );
}

function existingAttachments(detail: ReferralDetail | undefined, draftId: string | null) {
  if (!detail || detail.id !== draftId) return [];
  return detail.attachments;
}

function AddProviderInline({ practiceId, onAdded }: { practiceId: string; onAdded: (entry: DirectoryEntry) => void }) {
  const { addProvider } = useReferralMutations();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  if (!open)
    return (
      <button type="button" className={chips.addChip} onClick={() => setOpen(true)}>
        <Plus size={12} /> Add doctor
      </button>
    );
  return (
    <input
      autoFocus
      className={chips.inlineInput}
      placeholder="Dr. Name"
      value={name}
      onChange={(e) => setName(e.target.value)}
      onKeyDown={async (e) => {
        if (e.key === 'Escape') setOpen(false);
        if (e.key === 'Enter' && name.trim()) {
          const entry = await addProvider.mutateAsync({ practiceId, name: name.trim() });
          onAdded(entry);
          setName('');
          setOpen(false);
        }
      }}
      onBlur={() => !name.trim() && setOpen(false)}
    />
  );
}

/** How the slip reaches the office. Email when we have it; text or a printed slip when we do not. */
function DeliveryChoices({
  officeName,
  delivery,
  methods,
  onChange,
  onAddEmail,
}: {
  officeName: string;
  delivery: NonNullable<RecipientOption['delivery']>;
  methods: DeliveryMethod[];
  onChange: (next: DeliveryMethod[]) => void;
  onAddEmail?: () => void;
}) {
  const toggle = (m: DeliveryMethod) => onChange(methods.includes(m) ? methods.filter((x) => x !== m) : [...methods, m]);
  const row = (m: DeliveryMethod, label: string, detail: string, available: boolean, extra?: React.ReactNode) => (
    <label className={clsx(styles.deliveryRow, !available && styles.deliveryOff)}>
      <input type="checkbox" checked={methods.includes(m)} disabled={!available} onChange={() => toggle(m)} />
      <span className={styles.deliveryText}>
        <span className={styles.deliveryLabel}>{label}</span>
        <span className={styles.deliveryDetail}>
          {detail} {extra}
        </span>
      </span>
    </label>
  );
  return (
    <div className={styles.delivery}>
      <div className={styles.deliveryTitle}>How it reaches {officeName}</div>
      {delivery.inbox.available && (
        <p className={styles.deliveryInbox}>They are on PracticeNova: the referral lands in their Referrals inbox the moment you send.</p>
      )}
      {row(
        'email',
        'Email',
        delivery.email.available ? `Secure link to the slip, X-rays and documents, sent to ${delivery.email.to}.` : 'No email on file for this office.',
        delivery.email.available,
        !delivery.email.available && onAddEmail ? (
          <button type="button" className={styles.linkBtn} onClick={onAddEmail}>
            Add their email
          </button>
        ) : undefined,
      )}
      {row(
        'text',
        'Text their office',
        delivery.text.available
          ? `Sent at the same moment as the referral, from your texting number${delivery.text.from ? ` ${delivery.text.from}` : ''} to ${delivery.text.to}${
              delivery.text.lineType === 'mobile'
                ? ' (a mobile number)'
                : delivery.text.lineType === 'given'
                  ? ' (the mobile this office gave)'
                  : ' (an internet phone line; some do not receive texts)'
            }. It carries the same secure link; nothing to install, no account to make.`
          : (delivery.text.reason ?? 'Texting is not available.'),
        delivery.text.available,
      )}
      {row(
        'print',
        'Printed slip',
        'An office copy with a QR code to the secure link (fax, mail, or hand to the patient) and a patient copy with where to go. Print from the next screen.',
        true,
      )}
      {methods.length === 0 && <p className={styles.previewWarn}>Pick at least one way to deliver the referral to {officeName}.</p>}
    </div>
  );
}
